import authService from './auth.service';
import type {
  Skill,
  SkillListResponse,
  SkillCreateRequest,
  SkillUpdateRequest,
  SkillTestResponse,
  SkillAnalyticsResponse,
  SkillTemplateListResponse,
  SkillTemplateGenerateParams,
} from '../types/skill.types';

const API_URL = '/api/v1/agent/skills';

const TEST_TIMEOUT_MS = 60_000;

/** Structured error from skill endpoints; lets callsites branch on HTTP status. */
export class SkillApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'SkillApiError';
    this.status = status;
    this.detail = detail;
  }
}

async function handleResponse<T>(res: Response, fallbackMsg: string): Promise<T> {
  if (res.ok) {
    return (await res.json()) as T;
  }
  if (res.status === 429) {
    throw new SkillApiError(429, 'Слишком много запросов. Подождите немного.');
  }
  const err = await res.json().catch(() => null);
  const detail = typeof err?.detail === 'string' ? err.detail : fallbackMsg;
  throw new SkillApiError(res.status, detail);
}

class SkillService {
  async list(activeOnly: boolean = false): Promise<SkillListResponse> {
    const qs = new URLSearchParams({ active_only: String(activeOnly) });
    const res = await authService.fetchWithAuth(`${API_URL}?${qs}`);
    return handleResponse<SkillListResponse>(res, 'Не удалось загрузить навыки');
  }

  async get(id: number): Promise<Skill> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`);
    return handleResponse<Skill>(res, 'Не удалось загрузить навык');
  }

  async create(data: SkillCreateRequest): Promise<Skill> {
    const res = await authService.fetchWithAuth(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<Skill>(res, 'Не удалось создать навык');
  }

  async update(id: number, data: SkillUpdateRequest): Promise<Skill> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return handleResponse<Skill>(res, 'Не удалось обновить навык');
  }

  async delete(id: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/${id}`, {
      method: 'DELETE',
    });
    if (res.ok) return;
    if (res.status === 429) {
      throw new SkillApiError(429, 'Слишком много запросов. Подождите немного.');
    }
    const err = await res.json().catch(() => null);
    const detail = typeof err?.detail === 'string' ? err.detail : 'Не удалось удалить навык';
    throw new SkillApiError(res.status, detail);
  }

  async test(id: number): Promise<SkillTestResponse> {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TEST_TIMEOUT_MS);
    try {
      const res = await authService.fetchWithAuth(`${API_URL}/${id}/test`, {
        method: 'POST',
        signal: controller.signal,
      });
      return await handleResponse<SkillTestResponse>(res, 'Не удалось запустить тест');
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        throw new SkillApiError(
          0,
          `Тест превысил лимит времени (${Math.round(TEST_TIMEOUT_MS / 1000)} с). Попробуйте позже.`,
        );
      }
      throw e;
    } finally {
      window.clearTimeout(timer);
    }
  }

  async listTemplates(): Promise<SkillTemplateListResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/templates/list`);
    return handleResponse<SkillTemplateListResponse>(res, 'Не удалось загрузить шаблоны');
  }

  async generateFromTemplate(params: SkillTemplateGenerateParams): Promise<Skill> {
    const qs = new URLSearchParams({
      template_code: params.template_code,
      entity_code: params.entity_code,
      entity_name: params.entity_name,
    });
    const res = await authService.fetchWithAuth(`${API_URL}/templates/generate?${qs}`, {
      method: 'POST',
    });
    return handleResponse<Skill>(res, 'Не удалось создать навык из шаблона');
  }

  async getCommands(): Promise<{ commands: Array<{ command: string; name: string; description: string }> }> {
    const res = await authService.fetchWithAuth(`${API_URL}/commands`);
    return handleResponse(res, 'Не удалось загрузить команды');
  }

  async analytics(days: number = 7): Promise<SkillAnalyticsResponse> {
    const res = await authService.fetchWithAuth(`${API_URL}/analytics/usage?days=${days}`);
    return handleResponse<SkillAnalyticsResponse>(res, 'Не удалось загрузить аналитику');
  }
}

const skillService = new SkillService();
export default skillService;
